import React, {useEffect, useRef, useState} from 'react';
import gsap from 'gsap';
import StartStep from "btdt/react/steps/StartStep";
import AddressStep from "btdt/react/steps/AddressStep";
import ProjectTypeStep from "btdt/react/steps/ProjectTypeStep";
import SizesStep from "btdt/react/steps/SizesStep";
import ChooseLocationStep from "btdt/react/steps/ChooseLocationStep";
import NoLocationStep from "btdt/react/steps/NoLocationStep";
import ContactStep from "btdt/react/steps/ContactStep";
import BillingStep from "btdt/react/steps/BillingStep";
import CompleteStep from "btdt/react/steps/CompleteStep";
import MaterialsStep from "btdt/react/steps/MaterialsStep";
import OnlineOrderStep from "btdt/react/steps/OnlineOrderStep";
import {ajaxUrl} from "btdt/config";
import Loading from "btdt/react/Loading";
import {matchUserToLocation} from "btdt/shared";

export default function OrderForm({app}) {

    const referralLoc = useRef(null)
    const prefLoc = useRef(null)
    const prefProd = useRef(null)
    const prefProdName = useRef(null)

    // set preferred product when user arrives here by clicking a "rent this bin" button
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has('site') && urlParams.has('b_id') && urlParams.has('b_name')) {
        // if they clicked a button from the main site, set preferred name since we can't rely on matching product ID
        if (parseInt(urlParams.get('site')) === 1) {
            prefProdName.current = urlParams.get('b_name')
        // if they clicked a button from a franchise site, we should be able to match the product ID exactly
        } else {
            prefLoc.current = app.locations.find(l => l.id === urlParams.get('site'))
            prefProd.current = prefLoc.current?.products?.find(p => p.id === parseInt(urlParams.get('b_id')))
            if (!prefProd.current) {
                prefProdName.current = urlParams.get('b_name')
            }
        }
    }

    // set the userPlace from external (non-react) components if it has been set
    const extPlace = useRef(app.getState('userPlace'))

    // set the chosenLocation from external (non-react) components if it has been set
    const extLoc = useRef(app.getState('chosenLocation'))
    if (extLoc.current) {
        // reload the chosenLocation from the master list of locations in case the one in local storage is outdated
        const found = app.locations.find(l => l.site === extLoc.current.site)
        if (found) {
            extLoc.current = found
        }
    }

    // handle setup of simple contact form
    if (urlParams.has('f')) {
        referralLoc.current = app.locations.find(l => l.id === urlParams.get('f'))
    }
    const useSimpleForm = useRef(referralLoc.current && referralLoc.current.simplified_booking)
    const [simpleComplete, setSimpleComplete] = useState(false)

    const stepWrapper = useRef(null);

    // skip ahead to address step if they have a preferred location and/or product set
    const initialStep = useRef(((prefLoc.current && prefProd.current) || prefProdName.current) ? 'address' : 'start')
    const history = useRef([initialStep.current])

    const [currentStep, setCurrentStep] = useState(initialStep.current);
    const [animating, setAnimating] = useState(false);
    const [loading, setLoading] = useState(false);
    const [needSizingHelp, setNeedSizingHelp] = useState(false);
    const [needContactHelp, setNeedContactHelp] = useState(false);
    const [userPlace, setUserPlace] = useState( extPlace.current || null);
    const [projectType, setProjectType] = useState(null);
    const [projectSubType, setProjectSubType] = useState(null);
    const [locationChoices, setLocationChoices] = useState(null);
    const [chosenLocation, setChosenLocation] = useState( useSimpleForm.current ? referralLoc.current : (extLoc.current || null));
    const [chosenProduct, setChosenProduct] = useState(prefProd.current || null);
    const [materials, setMaterials] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const [userBillingInfo, setUserBillingInfo] = useState(null);
    const [sameBilling, setSameBilling] = useState(null);
    const [useBinTracker, setUseBinTracker] = useState(null);
    const [submitSuccessful, setSubmitSuccessful] = useState(null);

    const projectTypes = [
        {
            label: "Household Cleanout",
            id: "household",
            subTypes: [
                {
                    label: "1-2 rooms",
                    id: "smallcleanout",
                },
                {
                    label: "Garage or storage room only",
                    id: "garagecleanout",
                },
                {
                    label: "Multi-room/hoarder/estate cleanout",
                    id: "multicleanout",
                },
            ]
        },
        {
            label: "Remodel/Renovation",
            id: "reno",
            subTypes: [
                {
                    label: "Kitchen or larger room",
                    id: "bigreno",
                },
                {
                    label: "Bathroom or smaller room",
                    id: "smallreno",
                },
                {
                    label: "Multi-room, insulation, drywall/studs/cabinetry, etc.",
                    id: "multireno",
                },
            ]
        },
        {
            label: "Yard Project",
            id: "yard",
            subTypes: [
                {
                    label: "Decking",
                    id: "decking",
                },
                {
                    label: "Fencing",
                    id: "fencing",
                },
                {
                    label: "Patio landscaping",
                    id: "patio",
                },
            ]
        },
        {
            label: "Restoration: Fire/Flood",
            id: "restoration",
        },
        {
            label: "Roof project or heavy materials",
            id: "heavy",
        },
        {
            label: "Other projects not listed",
            id: "other",
        }
    ]

    useEffect(() => {
        if (userPlace && app.debug) {
            console.log(`user place set to: `, userPlace);
        }
    }, [userPlace]);

    useEffect(() => {
        if (projectType && app.debug) {
            console.log(`project type set to: `, projectType);
        }
    }, [projectType]);

    useEffect(() => {
        if (projectSubType && app.debug) {
            console.log(`project subtype set to: `, projectSubType);
        }
    }, [projectSubType]);

    useEffect(() => {
        if (chosenLocation && app.debug) {
            console.log(`chosen franchise set to: `, chosenLocation);
        }
    }, [chosenLocation]);

    useEffect(() => {
        if (chosenProduct && app.debug) {
            console.log(`chosen product set to: `, chosenProduct);
        }
    }, [chosenProduct]);

    useEffect(() => {
        if (materials !== null && app.debug) {
            console.log(`materials set to: `, materials);
        }
    }, [materials]);


    // useEffect(() => {
    //     if (userInfo && app.debug) {
    //         console.log(`user info set to: `, userInfo);
    //     }
    // }, [userInfo]);
    //
    // useEffect(() => {
    //     if (userBillingInfo) {
    //         console.log(`billing info set to: `, userBillingInfo);
    //     }
    // }, [userBillingInfo]);

    const goToStep = async (step, dir) => {
        setAnimating(true);
        await gsap.to(stepWrapper.current, {
            xPercent: (dir === 'back' ? 100 : -100), duration: 0.5, opacity: 0, ease: "back.in(0.75)"
        })
        if (dir !== 'back') {
            history.current = [...history.current, step]
        }

        setCurrentStep(step)
        window.scrollTo({ top: 0, behavior: 'smooth' });
        await gsap.fromTo(stepWrapper.current, {
            xPercent: 0, opacity: 0, scale: 0.9
        }, {
            opacity: 1, ease: "expo.out", scale: 1, duration: 0.25
        })
        setAnimating(false);
    }

    const goBack = async () => {
        const newHistory = Array.from(history.current)
        newHistory.splice(-1)
        let last = newHistory.at(-1)
        history.current = newHistory
        // if we get into a broken history state, just bring them back to the start
        if (!last) {
            last = 'start'
        }
        await goToStep(last, 'back')
    }

    const startHelp = async () => {
        setNeedSizingHelp(true)
        setNeedContactHelp(false)
        await goToStep('address')
    }

    const bookNow = async () => {
        setNeedSizingHelp(false)
        setNeedContactHelp(false)
        await goToStep('address')
    }

    const sendMessage = async () => {
        if (chosenLocation) {
            window.location.href = chosenLocation.site + 'contact/'
        } else {
            setNeedContactHelp(true)
            await goToStep('address')
        }
    }

    const finishAddressStep = async () => {
        if (locationChoices?.length > 1) {
            await goToStep('chooseLocation')
        } else if (locationChoices?.length === 0) {
            await goToStep('noLocation')
        } else {
            await finishLocationStep()
        }
    }

    const finishLocationStep = async () => {
        if (needContactHelp && chosenLocation) {
            window.location.href = chosenLocation.site + 'contact/'
            return
        }

        if (needSizingHelp) {
            await goToStep('projectType')
        } else {
            if (chosenLocation?.online_ordering && chosenLocation?.order_page) {
                await goToStep('onlineOrder')
            } else {
                await goToStep('sizes')
            }
        }
    }

    const finishOnlineStep = async () => {
        if (useBinTracker) {
            window.location.href = chosenLocation.order_page
        } else {
            await goToStep('sizes')
        }
    }

    const finishProjectTypeStep = async () => {
        if (projectType.id === 'heavy') {
            await goToStep('contact')
        } else if (projectType.subTypes) {
            await goToStep('projectSubType')
        } else {
            await goToStep('sizes')
        }
    }

    const finishProjectSubTypeStep = async () => {
        await goToStep('sizes')
    }

    const finishSizeStep = async () => {
        if (needSizingHelp) {
            await goToStep('contact')
        } else {
            await goToStep('materials')
        }
    }

    const finishMaterialsStep = async () => {
        await goToStep('contact')
    }

    const finishContactStep = async () => {

        if (useSimpleForm.current) {
            await submitForm()
            setSimpleComplete(true)
        } else {
            if (chosenLocation.show_billing) {
                await goToStep('billing')
            } else {
                await submitForm()
                await goToStep('complete')
            }
        }
    }

    const finishBillingStep = async () => {
        await submitForm()
        await goToStep('complete')
    }

    const submitForm = async () => {

        let finalLocation = chosenLocation

        // if using the simple form, try to catch franchise mismatches before submitting
        if (useSimpleForm.current && !finalLocation.addr_not_required) {
            const matches = await matchUserToLocation(userInfo, app.locations, app.google)
            if (matches.length === 1) {
                if (app.debug) console.log('correcting matched location to', matches[0])
                finalLocation = matches[0]
                setChosenLocation(matches[0])
            } else {
                if (app.debug) console.log('better location match not found, using original')
            }
        }

        const formData = new FormData();

        formData.set('action', 'submit_order_form')

        formData.set('location', finalLocation?.id || '')
        formData.set('product', chosenProduct?.title || '')

        formData.set('name', userInfo?.name || '')
        formData.set('phone', userInfo?.phone || '')
        formData.set('email', userInfo?.email || '')
        formData.set('country', userInfo?.country || '')

        formData.set('drop_off', userInfo?.dropOff || '')
        formData.set('date', userInfo?.date || '')

        formData.set('street', userInfo?.street || '')
        formData.set('city', userInfo?.city || '')
        formData.set('region', userInfo?.region || '')
        formData.set('postal_code', userInfo?.postalCode || '')

        formData.set('same_billing', sameBilling || true)
        formData.set('billing_street', userBillingInfo?.street || '')
        formData.set('billing_city', userBillingInfo?.city || '')
        formData.set('billing_region', userBillingInfo?.region || '')
        formData.set('billing_postal_code', userBillingInfo?.postalCode || '')

        formData.set('materials', materials || '')
        formData.set('project_type', projectType?.label || '')
        formData.set('project_subtype', projectSubType?.label || '')

        setLoading(true)
        setSubmitSuccessful(null)

        try {
            const response = await fetch(ajaxUrl, {
                method: "POST", body: formData,
            })

            const result = await response.json();

            if (result.status === 'success') {

                setSubmitSuccessful(true)

                if (window.analytics) {
                    analytics.track('Quote Requested', {
                        affiliation: finalLocation?.location_name, products: [{
                            title: chosenProduct?.title,
                            size: chosenProduct?.fields?.size,
                            price: chosenProduct?.fields?.price,
                            quantity: 1,
                        }]
                    })
                }

                window.dataLayer = window.dataLayer || []
                window.dataLayer.push({
                    'event': "formSubmission",
                    'formID': 1,
                    'formName': 'Request a Dumpster Form',
                    'formFranchise': finalLocation?.location_name
                })
            } else {
                setSubmitSuccessful(false)
            }

        } catch (e) {
            console.warn(e)
            setSubmitSuccessful(false)
        }
        setLoading(false)

    }

    // handle when the user chooses a new place from the address dropdown
    useEffect(() => {
        // pass the new userPlace back to non-react components
        app.setState('userPlace', userPlace)
        // reset the saved user contact info when choosing a new place
        setUserInfo(null)
        setUserBillingInfo(null)
    }, [userPlace]);

    // handle when the user chooses a new franchise
    useEffect(() => {
        // pass the new chosenLocation back to non-react components
        app.setState('chosenLocation', chosenLocation)

        // automatically set the chosen product by name if a preferred product exists
        if (chosenLocation && prefProdName.current) {
            setChosenProduct(chosenLocation.products?.find(p => p.title === prefProdName.current))
        }
    }, [chosenLocation]);

    const steps = {
        start: <StartStep
            animating={animating}
            handleStartHelp={startHelp}
            handleSkipHelp={bookNow}
            handleSendMessage={sendMessage}
        />,
        address: <AddressStep
            app={app}
            animating={animating}
            place={userPlace}
            handleLoading={setLoading}
            chosenLocation={chosenLocation}
            needContactHelp={needContactHelp}
            handleLocationMatches={setLocationChoices}
            handleLocationChange={setChosenLocation}
            handlePlaceChange={setUserPlace}
            handleNextStep={finishAddressStep}
        />,
        projectType: <ProjectTypeStep
            key={'type'}
            animating={animating}
            title={"What kind of project are you doing? (Choose one)"}
            types={projectTypes}
            chosenLocation={chosenLocation}
            chosenType={projectType}
            handleTypeChange={setProjectType}
            handleNextStep={finishProjectTypeStep}
        />,
        projectSubType: <ProjectTypeStep
            key={'subtype'}
            animating={animating}
            title={"What size of project are you doing? (Choose one)"}
            types={projectType?.subTypes}
            chosenLocation={chosenLocation}
            chosenType={projectSubType}
            handleTypeChange={setProjectSubType}
            handleNextStep={finishProjectSubTypeStep}
        />,
        chooseLocation: <ChooseLocationStep
            animating={animating}
            locations={locationChoices}
            needContactHelp={needContactHelp}
            chosenLocation={chosenLocation}
            handleLocationChange={setChosenLocation}
            handleNextStep={finishLocationStep}
        />,
        noLocation: <NoLocationStep
            animating={animating}
        />,
        onlineOrder: <OnlineOrderStep
            animating={animating}
            chosenLocation={chosenLocation}
            useBinTracker={useBinTracker}
            handleBinTrackerChange={setUseBinTracker}
            handleNextStep={finishOnlineStep}
        />,
        sizes: <SizesStep
            simpleMode={useSimpleForm.current}
            animating={animating}
            chosenLocation={chosenLocation}
            chosenProduct={chosenProduct}
            projectType={projectType}
            projectSubType={projectSubType}
            preferredLocation={prefLoc.current}
            preferredProduct={prefProdName.current}
            handleProductChange={setChosenProduct}
            handleNextStep={finishSizeStep}
        />,
        materials: <MaterialsStep
            simpleMode={useSimpleForm.current}
            animating={animating}
            chosenLocation={chosenLocation}
            materials={materials}
            handleMaterialsChange={setMaterials}
            handleNextStep={finishMaterialsStep}
        />,
        contact: <ContactStep
            simpleMode={useSimpleForm.current}
            animating={animating}
            place={userPlace}
            projectType={projectType}
            chosenLocation={chosenLocation}
            userInfo={userInfo}
            handleInfoChange={setUserInfo}
            handleNextStep={finishContactStep}
        />,
        billing: <BillingStep
            animating={animating}
            sameBilling={sameBilling}
            chosenLocation={chosenLocation}
            billingInfo={userBillingInfo}
            handleBillingInfoChange={setUserBillingInfo}
            handleSameBillingChange={setSameBilling}
            handleFormSubmit={finishBillingStep}
        />,
        complete: <CompleteStep
            simpleMode={useSimpleForm.current}
            animating={animating}
            chosenLocation={chosenLocation}
            successful={submitSuccessful}
            handleStarOver={async () => await goToStep('start', 'back')}
        />,
    }

    const step = steps[currentStep]

    if (useSimpleForm.current) {
        return (
            <>
                <div className={"grow flex flex-col justify-center relative py-12 space-y-12"}>

                    {!simpleComplete ? (
                        <>
                            <div className={"container mx-auto px-4 lg:px-8"}>
                                <div className={"grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-6"}>
                                    <div className={"lg:col-span-12"}>
                                        <h1 className="text-[35px] leading-[42px] lg:big-text text-center pb-6">
                                            Reserve A Dumpster
                                        </h1>
                                    </div>
                                    <div className={"lg:col-span-8 lg:col-start-3"}>
                                        <div className={"font-bold"}>
                                            Got questions? Give us a call at <a href={`tel:${chosenLocation.phone}`} className={"whitespace-nowrap !text-brand-blue !decoration-brand-blue hover:!decoration-transparent"}>{chosenLocation.phone}</a> and we can help you out!
                                        </div>

                                        {chosenLocation && chosenLocation?.pricing_info && (
                                            <ul>
                                                {chosenLocation.pricing_info.map(i => (
                                                    <li>{i.line}</li>
                                                ))}
                                            </ul>
                                        )}

                                    </div>
                                </div>
                            </div>

                            <div>
                                {steps['sizes']}
                            </div>

                            <div>
                                {steps['materials']}
                            </div>

                            <div>
                                {steps['contact']}
                            </div>
                        </>
                    ) : (
                        <div>
                            {steps['complete']}
                        </div>
                    )}

                    {loading && <Loading/>}
                </div>

            </>
        )
    }


    return (
        <>
            <div ref={stepWrapper} className={"grow flex flex-col justify-center relative py-6 lg:py-12"}>
                {step ? step : (<div>
                    Step "{currentStep}" not found
                </div>)}

                {currentStep !== 'start' && currentStep !== 'complete' && (
                    <div className={"container mx-auto px-4 lg:px-8 mt-8"}>
                        <div className={"grid grid-cols-1 lg:grid-cols-12 gap-x-8"}>
                            <div className={"lg:col-span-8 lg:col-start-3"}>
                                <button onClick={goBack} className={"btn p-0 !bg-transparent !min-h-[auto] border-none underline underline-offset-4 decoration-2 decoration-transparent hover:decoration-current text-brand-dark hover:!text-brand-blue"} disabled={animating}>
                                    ← Go Back A Step
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {loading && <Loading/>}
            </div>

        </>
    )
}